import { useQuery } from '@tanstack/react-query';
import { ListFilter, SlidersHorizontal } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { listChannels } from '@/features/channels/api';
import { listDepartments, listUsers } from '@/features/configuracoes/api';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { TicketsScope } from '../api';

interface Props {
  /** Mostra "Visualizar todos" + Usuário — só admin em Ativos/Fechados.
   *  Departamento e Canal aparecem sempre (todos os perfis, 3 abas). */
  showScope: boolean;
  /** Mostra os filtros de triagem (não respondidos / não lidas) — só em Ativos. */
  showTriage: boolean;
  scope: TicketsScope;
  onScopeChange: (s: TicketsScope) => void;
  selectedUserId: number | null;
  onSelectedUserIdChange: (id: number | null) => void;
  selectedDepartmentId: number | null;
  onSelectedDepartmentIdChange: (id: number | null) => void;
  selectedChannelId: number | null;
  onSelectedChannelIdChange: (id: number | null) => void;
  onlyUnread: boolean;
  onOnlyUnreadChange: (v: boolean) => void;
  onlyUnanswered: boolean;
  onOnlyUnansweredChange: (v: boolean) => void;
  /** Quando true, o botão vira um ícone solto (fora do campo de busca) e o
   *  painel abre alinhado à direita do próprio botão. Default: dentro do campo. */
  standalone?: boolean;
}

/**
 * Quantos filtros estão ligados NA ABA ABERTA — o número que aparece no botão.
 * O estado dos filtros é um só pras 3 abas, mas só conta o que vale (e aparece)
 * aqui: "Visualizar todos" e usuário só onde há showScope (admin em Ativos/
 * Fechados), triagem só em Ativos (showTriage); departamento e canal valem em
 * todas. Antes, o "Visualizar todos" ligado em Ativos acendia "1" em Pendentes
 * sem nenhum filtro à vista (dono, 18/out/2026).
 */
export function contarFiltrosAtivos(f: {
  showScope: boolean;
  showTriage: boolean;
  scope: TicketsScope;
  selectedUserId: number | null;
  selectedDepartmentId: number | null;
  selectedChannelId: number | null;
  onlyUnread: boolean;
  onlyUnanswered: boolean;
}): number {
  const todos = f.showScope && f.scope === 'all';
  return (
    (todos ? 1 : 0) +
    (f.selectedDepartmentId !== null ? 1 : 0) +
    (todos && f.selectedUserId !== null ? 1 : 0) +
    (f.selectedChannelId !== null ? 1 : 0) +
    (f.showTriage && f.onlyUnread ? 1 : 0) +
    (f.showTriage && f.onlyUnanswered ? 1 : 0)
  );
}

/**
 * Popover de filtros avançados da inbox — nas 3 abas, pra todos os perfis.
 * Departamento e Canal pra todo mundo (atendente só vê os departamentos dele;
 * o servidor aplica o acesso por cima). "Visualizar todos" + Usuário só admin
 * em Ativos/Fechados; triagem só em Ativos.
 *
 * Estado:
 * - Toggle OFF (scope='mine') — vê só os tickets do user logado. Select de
 *   usuário fica desabilitado.
 * - Toggle ON sem usuário (scope='all', selectedUserId=null) — vê todas as
 *   conversas ativas do tenant.
 * - Toggle ON + usuário selecionado — filtra por aquele usuário específico.
 *
 * O botão mora DENTRO do campo de busca, à direita (opção A do canvas "Busca e
 * filtro da inbox", 12/out/2026). Sem filtro: só o ícone, discreto. Com filtro:
 * pílula azul clara com o NÚMERO de filtros ligados — antes era um quadrado
 * azul sólido que não dizia quantos nem o quê.
 */
export function FilterPopover({
  showScope,
  showTriage,
  scope,
  onScopeChange,
  selectedUserId,
  onSelectedUserIdChange,
  selectedDepartmentId,
  onSelectedDepartmentIdChange,
  selectedChannelId,
  onSelectedChannelIdChange,
  onlyUnread,
  onOnlyUnreadChange,
  onlyUnanswered,
  onOnlyUnansweredChange,
  standalone = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);
  // Admin e suporte enxergam todos os departamentos; atendente, só os dele.
  const veTodosDepartamentos = user?.profile === 'admin' || Boolean(user?.impersonating);

  const usersQ = useQuery({
    queryKey: ['settings-users'],
    queryFn: () => listUsers(),
    enabled: open && showScope && scope === 'all', // só quando abre + toggle ON
    staleTime: 30_000,
  });

  const departmentsQ = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: open,
    staleTime: 30_000,
  });

  const channelsQ = useQuery({
    queryKey: ['channels'],
    queryFn: listChannels,
    enabled: open,
    staleTime: 30_000,
  });

  const departmentOptions = (departmentsQ.data ?? []).filter(
    (d) => veTodosDepartamentos || (user?.departmentIds ?? []).includes(d.id),
  );

  // Click fora fecha. Listener global, ativo apenas quando aberto.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const scopeOn = scope === 'all';
  const ativos = contarFiltrosAtivos({
    showScope,
    showTriage,
    scope,
    selectedUserId,
    selectedDepartmentId,
    selectedChannelId,
    onlyUnread,
    onlyUnanswered,
  });

  function handleToggle(): void {
    const next: TicketsScope = scope === 'all' ? 'mine' : 'all';
    onScopeChange(next);
    // Voltando pra 'mine', "de quem" deixa de fazer sentido (são os meus).
    // Departamento e canal seguem valendo — "meus, do departamento X".
    if (next === 'mine') onSelectedUserIdChange(null);
  }

  function handleUserChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const value = e.target.value;
    onSelectedUserIdChange(value === '' ? null : Number(value));
  }

  // Cascata departamento → usuário: com um dept selecionado, o select de
  // usuário só lista quem PERTENCE a ele (user_departments). Sem dept, todos.
  const userOptions = (usersQ.data ?? [])
    .filter((u) => u.status === 'active')
    .filter(
      (u) =>
        selectedDepartmentId === null ||
        u.departments.some((d) => d.id === selectedDepartmentId),
    );

  function handleDepartmentChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const value = e.target.value;
    const deptId = value === '' ? null : Number(value);
    onSelectedDepartmentIdChange(deptId);
    // Usuário selecionado fora do dept novo → limpa (não dá pra filtrar por
    // quem não faz parte do departamento).
    if (deptId !== null && selectedUserId !== null) {
      const stillValid = (usersQ.data ?? []).some(
        (u) => u.id === selectedUserId && u.departments.some((d) => d.id === deptId),
      );
      if (!stillValid) onSelectedUserIdChange(null);
    }
  }

  return (
    // `contents`: o wrapper não posiciona nada — o botão encosta na direita do
    // campo de busca e o painel abre alinhado à ESQUERDA do campo (o pai
    // `relative` é o campo). Ancorado no botão, ele vazaria pra fora da lista.
    <div className={standalone ? 'relative' : 'contents'} ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-pressed={ativos > 0}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Filtros avançados"
        aria-label={
          ativos > 0
            ? `Filtros avançados: ${ativos} ${ativos === 1 ? 'ativo' : 'ativos'}`
            : 'Filtros avançados'
        }
        className={
          standalone
            ? `flex h-[33px] items-center justify-center rounded-md transition-colors ${
                ativos > 0
                  ? 'gap-[5px] bg-brand-50 px-[8px] font-bold text-[11px] text-brand-600 hover:bg-brand-100'
                  : `w-[34px] text-n-600 hover:bg-n-100 hover:text-n-800 ${open ? 'bg-n-100' : ''}`
              }`
            : `-translate-y-1/2 absolute top-1/2 right-[4px] flex h-[26px] items-center justify-center rounded-pill transition-colors ${
                ativos > 0
                  ? 'gap-[5px] bg-brand-50 px-[8px] font-bold text-[11px] text-brand-600 hover:bg-brand-100'
                  : `w-[26px] text-n-500 hover:bg-n-100 hover:text-n-700 ${open ? 'bg-n-100' : ''}`
              }`
        }
      >
        {/* Fora do campo usa o funil de 3 linhas (ListFilter); dentro do campo
            segue o SlidersHorizontal de antes. */}
        {standalone ? (
          <ListFilter size={ativos > 0 ? 15 : 18} strokeWidth={1.9} />
        ) : (
          <SlidersHorizontal size={ativos > 0 ? 14 : 15} strokeWidth={ativos > 0 ? 2 : 1.75} />
        )}
        {ativos > 0 && ativos}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Filtros avançados"
          className={`absolute top-[calc(100%+6px)] z-30 w-[280px] rounded-md border border-n-200 bg-n-0 p-3 shadow-md ${
            standalone ? 'right-0' : 'left-0'
          }`}
        >
          <div className="mb-2 font-semibold text-[12px] text-n-700">Filtros Avançados</div>

          {/* "Visualizar todos" — admin ou perfil com a permissão, em
              Ativos/Fechados (showScope). */}
          {showScope && (
            <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5">
              <span className="text-[13px] text-n-700">
                {user?.profile === 'admin' && <span className="text-n-500">(Admin)</span>}{' '}
                Visualizar todos
              </span>
              <button
                type="button"
                onClick={handleToggle}
                role="switch"
                aria-checked={scopeOn}
                aria-label="Visualizar todos"
                className={`relative inline-flex h-[18px] w-[32px] flex-shrink-0 items-center rounded-pill transition-colors ${
                  scopeOn ? 'bg-brand-500' : 'bg-n-300'
                }`}
              >
                <span
                  className={`pointer-events-none absolute h-[14px] w-[14px] rounded-pill bg-n-0 shadow-sm transition-transform ${
                    scopeOn ? 'translate-x-[16px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Departamento — todos os perfis, 3 abas. Vem ANTES do usuário: estreita
              a lista de usuários (cascata). Atendente só vê os departamentos dele. */}
          <div className={showScope ? 'mt-3' : ''}>
            <label className="mb-1 block font-medium text-[11px] text-n-500 uppercase tracking-wide">
              Departamento
            </label>
            <select
              value={selectedDepartmentId ?? ''}
              onChange={handleDepartmentChange}
              className="h-[34px] w-full rounded-md border border-n-200 bg-n-0 px-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-n-50 disabled:text-n-400"
            >
              <option value="">Todos os departamentos</option>
              {departmentOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Canal — todos os perfis, 3 abas. */}
          <div className="mt-3">
            <label className="mb-1 block font-medium text-[11px] text-n-500 uppercase tracking-wide">
              Canal
            </label>
            <select
              value={selectedChannelId ?? ''}
              onChange={(e) =>
                onSelectedChannelIdChange(e.target.value === '' ? null : Number(e.target.value))
              }
              className="h-[34px] w-full rounded-md border border-n-200 bg-n-0 px-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-n-50 disabled:text-n-400"
            >
              <option value="">Todos os canais</option>
              {(channelsQ.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName || c.displayNumber || `Canal ${c.id}`}
                </option>
              ))}
            </select>
          </div>

          {/* Usuário — só admin em Ativos/Fechados, com "Visualizar todos" ligado.
              Cascateado: só quem pertence ao departamento acima. */}
          {showScope && (
            <div className="mt-3">
              <label className="mb-1 block font-medium text-[11px] text-n-500 uppercase tracking-wide">
                Usuário
              </label>
              <select
                value={selectedUserId ?? ''}
                onChange={handleUserChange}
                disabled={!scopeOn}
                className="h-[34px] w-full rounded-md border border-n-200 bg-n-0 px-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:bg-n-50 disabled:text-n-400"
              >
                <option value="">Todos os usuários</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              {!scopeOn && (
                <p className="mt-1.5 text-[11px] text-n-500">
                  Habilite "Visualizar todos" para filtrar por usuário.
                </p>
              )}
            </div>
          )}

          {/* Divisória entre os filtros de lista e a triagem (só em Ativos). */}
          {showTriage && <div className="my-3 border-n-100 border-t" />}

          {/* Triagem — SÓ em Ativos (showTriage), disponível pra TODOS os perfis.
              Complementares: "não respondidos" = atendente viu (unread 0) mas não
              respondeu; "não lidas" = ainda não abriu (unread > 0). */}
          {showTriage && (
            <>
              <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5">
                <span className="text-[13px] text-n-700">Tickets não respondidos</span>
                <button
                  type="button"
                  onClick={() => onOnlyUnansweredChange(!onlyUnanswered)}
                  role="switch"
                  aria-checked={onlyUnanswered}
                  aria-label="Tickets não respondidos"
                  className={`relative inline-flex h-[18px] w-[32px] flex-shrink-0 items-center rounded-pill transition-colors ${
                    onlyUnanswered ? 'bg-brand-500' : 'bg-n-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none absolute h-[14px] w-[14px] rounded-pill bg-n-0 shadow-sm transition-transform ${
                      onlyUnanswered ? 'translate-x-[16px]' : 'translate-x-[2px]'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5">
                <span className="text-[13px] text-n-700">Tickets com mensagens não lidas</span>
                <button
                  type="button"
                  onClick={() => onOnlyUnreadChange(!onlyUnread)}
                  role="switch"
                  aria-checked={onlyUnread}
                  aria-label="Tickets com mensagens não lidas"
                  className={`relative inline-flex h-[18px] w-[32px] flex-shrink-0 items-center rounded-pill transition-colors ${
                    onlyUnread ? 'bg-brand-500' : 'bg-n-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none absolute h-[14px] w-[14px] rounded-pill bg-n-0 shadow-sm transition-transform ${
                      onlyUnread ? 'translate-x-[16px]' : 'translate-x-[2px]'
                    }`}
                  />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
